import type { BaseUser } from "@models";
import type { TiptapCollabProvider } from "@tiptap-pro/provider";
import type { Editor } from "@tiptap/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  TiptapSelectionToolbar,
  useTiptapComments,
  useTiptapSuggestions,
  type TiptapCommentUser,
  type TiptapCommentsController,
  type TiptapSuggestionsController,
  type TiptapThreadClickEvent,
} from "src/components/TiptapEditor/collaboration";
import { requestSelectedDynamicNodeEdit } from "src/tiptap/components/tiptap-node/dynamic-nodes/dynamic-node-selection";
import { getReadyEditor } from "./editor-readiness";

type ShouldDisableTiptapCommentsParams = {
  areCommentMutationsDisabled: boolean;
  hasEditor: boolean;
  hasProvider: boolean;
  isCollaborative: boolean;
};

export const shouldDisableTiptapComments = (
  params: ShouldDisableTiptapCommentsParams
) => {
  // Suggestion mode is intentionally not a comment lock.
  return (
    !params.isCollaborative ||
    !params.hasEditor ||
    params.areCommentMutationsDisabled ||
    !params.hasProvider
  );
};

export type SimpleEditorCollaborationExternalProps = {
  areCommentMutationsDisabled?: boolean;
  onCommentCreated?: (
    threadId: string,
    commentContent: string,
    previousCommentContent?: string
  ) => void;
  onCommentsControllerChange?: (
    controller: TiptapCommentsController | null
  ) => void;
  onCreateVersionHandlerChange?: (
    handler: (() => Promise<void>) | null
  ) => void;
  onOpenCommentsPanel?: () => void;
  onSuggestionsControllerChange?: (
    controller: TiptapSuggestionsController | null
  ) => void;
  orgUsers?: BaseUser[];
};

type UseSimpleEditorCollaborationOptions =
  SimpleEditorCollaborationExternalProps & {
    clickedThread: TiptapThreadClickEvent | null;
    currentUserEmail?: string | null;
    currentUserId: string;
    currentUserName: string;
    editor: Editor | null;
    isCollaborative: boolean;
    isReadOnly: boolean;
    isSavingOfficialVersion: boolean;
    onSaveOfficialVersion?: () => Promise<void>;
    provider: TiptapCollabProvider | null;
  };

export const useSimpleEditorCollaboration = ({
  areCommentMutationsDisabled = false,
  clickedThread,
  currentUserEmail = null,
  currentUserId,
  currentUserName,
  editor,
  isCollaborative,
  isReadOnly,
  isSavingOfficialVersion,
  onCommentCreated,
  onCommentsControllerChange,
  onCreateVersionHandlerChange,
  onOpenCommentsPanel,
  onSaveOfficialVersion,
  onSuggestionsControllerChange,
  orgUsers = [],
  provider,
}: UseSimpleEditorCollaborationOptions) => {
  const [saveVersionError, setSaveVersionError] = useState<string | null>(null);
  const activeEditorRef = useRef<Editor | null>(null);
  const isCreatingVersionRef = useRef(false);

  const currentUser = useMemo<TiptapCommentUser>(
    () => ({
      id: currentUserId,
      name: currentUserName,
      email: currentUserEmail,
    }),
    [currentUserEmail, currentUserId, currentUserName]
  );

  useEffect(() => {
    activeEditorRef.current = editor;
  }, [editor]);

  const openCommentsPanel = useCallback(() => {
    onOpenCommentsPanel?.();
  }, [onOpenCommentsPanel]);

  const commentsController = useTiptapComments({
    clickedThread,
    currentUser,
    disabled: shouldDisableTiptapComments({
      areCommentMutationsDisabled,
      hasEditor: Boolean(editor),
      hasProvider: Boolean(provider),
      isCollaborative,
    }),
    editor,
    onCommentCreated,
    onThreadCreated: openCommentsPanel,
    onThreadSelected: openCommentsPanel,
    orgUsers,
    provider,
  });

  useEffect(() => {
    // react-doctor-disable-next-line react-doctor/no-pass-data-to-parent -- The collaboration side panel is outside the editor pane, so the editor publishes its comment controller across this boundary.
    onCommentsControllerChange?.(commentsController);
  }, [commentsController, onCommentsControllerChange]);

  useEffect(() => {
    return () => {
      onCommentsControllerChange?.(null);
    };
  }, [onCommentsControllerChange]);

  const suggestionsController = useTiptapSuggestions({
    disabled: isReadOnly || !editor?.isEditable,
    editor,
  });

  useEffect(() => {
    // react-doctor-disable-next-line react-doctor/no-pass-data-to-parent -- The collaboration side panel is outside the editor pane, so the editor publishes its suggestion controller across this boundary.
    onSuggestionsControllerChange?.(suggestionsController);
  }, [onSuggestionsControllerChange, suggestionsController]);

  useEffect(() => {
    return () => {
      onSuggestionsControllerChange?.(null);
    };
  }, [onSuggestionsControllerChange]);

  const createVersion = useCallback(async () => {
    const activeEditor = getReadyEditor(activeEditorRef.current);

    if (
      !activeEditor ||
      !onSaveOfficialVersion ||
      isSavingOfficialVersion ||
      isCreatingVersionRef.current
    ) {
      return;
    }

    try {
      isCreatingVersionRef.current = true;
      setSaveVersionError(null);
      await onSaveOfficialVersion();
      getReadyEditor(activeEditorRef.current)?.commands.fetchVersions();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Unable to save version.";
      setSaveVersionError(message);
    } finally {
      isCreatingVersionRef.current = false;
    }
  }, [isSavingOfficialVersion, onSaveOfficialVersion]);

  useEffect(() => {
    if (!onCreateVersionHandlerChange) {
      return;
    }

    if (
      !getReadyEditor(editor) ||
      !isCollaborative ||
      isReadOnly ||
      !onSaveOfficialVersion
    ) {
      onCreateVersionHandlerChange(null);
      return;
    }

    onCreateVersionHandlerChange(createVersion);

    return () => {
      onCreateVersionHandlerChange(null);
    };
  }, [
    createVersion,
    editor,
    isCollaborative,
    isReadOnly,
    onCreateVersionHandlerChange,
    onSaveOfficialVersion,
  ]);

  return useMemo(
    () => ({
      commentsController,
      createVersion,
      refreshSuggestions: suggestionsController.refreshSuggestions,
      saveVersionError,
      suggestionsController,
    }),
    [commentsController, createVersion, saveVersionError, suggestionsController]
  );
};

export const SimpleEditorCollaborationSelectionToolbar = ({
  controller,
  editor,
}: {
  controller: TiptapCommentsController;
  editor: Editor | null;
}) => (
  <TiptapSelectionToolbar
    controller={controller}
    onEditSelection={
      editor ? () => requestSelectedDynamicNodeEdit(editor) : undefined
    }
  />
);
